import { LockOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Form, Image, Input, Select, Tag, Typography, Upload } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React from 'react';
import { NoteImage } from '../NoteImage';
import { dashboardBox, DASHBOARD_SECTIONS } from './dashboardMap';
import {
  BoxLabel,
  currencyInput,
  Field,
  fileListFromEvent,
  infoInput,
  monthsInput,
  NOTE_TYPES,
  Section,
  SensitiveLabel,
  SystemLabel,
} from './fields';
import { isTimelineWritable, NoteUpdates } from './NoteUpdates';
import { asideLines, FIELDS } from './noteSteps';

const { Option } = Select;
const { Text } = Typography;

// HOW a box of the note form looks, one note at a time — the half of the wizard that answers "what does
// this column look like on THIS note": which lock closed it and why (the same reading the backend does),
// what label carries that reason, what hangs under it, and which input kind paints it. Plus the two
// panels that sit beside the fields: the section's real screenshot for the Individual Note, and the
// "where does the rest come from" card for the products the system mints.
//
// It renders nothing on its own: `renderGroup`/`renderField` are called by NoteWizard with a `ctx`
// (mode, investment, locks, tags, span) and drop Form.Items into the wizard's one <Form>.

// Same reading as the backend (`investment-field-locks`): absent, null and a blank box are one value —
// "not set". 0 is a value.
const isUnset = (value) => value == null || (typeof value === 'string' && value.trim() === '');

const SCHEDULER_OWNED = 'Set by the note-cycle scheduler';
// The door the backend closes with INVESTMENT_FIELD_LOCKED: what SIZES and PRICES the note (amounts,
// yield, term) closes with its first ledger entry. A column nobody ever filled (legacy notes) may be
// filled once, so it only locks while it holds a value.
const LEDGER_FIXED_WHY =
  'Fixed: investor flows read this live from the note (the note already has ledger activity)';
// The two amounts are NOT the same thing (TL, 2026-09-10): `fundingAmount` is how much of the loan is put
// up for sale — the cap the rails assign every purchase against, painted TOTAL LOAN AMOUNT — while
// `investmentAmount` is the loan itself, painted below it as INITIAL LOAN AMOUNT. Measured on the prod
// dump, the offered cap is the SMALLER of the two on 11 of the 12 notes where they differ. Each box now
// wears the investor's word and says underneath what it does (dashboardMap); these two are the tooltips
// on their `sensitive` mark.
const FUNDING_WHY =
  'The cap investors can fill: the funding rails assign every purchase against it. 0 means closed. It is a slice of the loan below, not the whole loan.';
const INITIAL_AMOUNT_WHY =
  'The size of the loan behind the note, informative on the fact sheet. Alto reads it as the offering valuation cap.';
const NET_YIELD_WHY =
  'The rate investors are paid. On a Rolling Note every change is recorded in the yield history.';
const CYCLE_YIELD_WHY = 'Comes from the cycle configuration (Retool), not from this form';

// The numbered pin: on a label before the words, on the screenshot over the element it names.
function Marker({ n, style }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: 9,
        background: '#1890ff',
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '18px',
        ...style,
      }}
    >
      {n}
    </span>
  );
}

function StoredDocuments({ documents }) {
  if (isEmpty(documents)) return 'No documents';
  return documents.map((item) => (
    <div key={item._id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <a href={item.url} target="_blank" rel="noopener noreferrer">
        {item.name}
      </a>
      <Tag color={item.isAttached ? 'green' : 'default'}>{item.isAttached ? 'Attached' : 'Not attached'}</Tag>
    </div>
  ));
}

// Which columns this note no longer lets the operator move, and WHY — the same reading the backend does
// (`investment/modules/admin/mapping/investment-field-locks.ts`): the STORED value is what closes a door,
// so a saved fill locks the box on the refetch and an untouched hole stays open. Nothing is locked while
// creating: there is no note yet.
export function noteLocks(investment, mode) {
  const noteType = investment?.noteType;
  const creating = mode === 'create';
  const fixedByLedger = (field) =>
    !creating && investment?.hasLedgerActivity === true && !isUnset(investment?.[field]);
  const constitution = !creating && noteType === NOTE_TYPES.constitution;
  const rolling = !creating && noteType === NOTE_TYPES.rolling;
  return {
    noteType,
    constitution,
    rolling,
    fundingAmount: fixedByLedger('fundingAmount'),
    investmentAmount: fixedByLedger('investmentAmount'),
    termLength: fixedByLedger('termLength'),
    maturityDate: fixedByLedger('maturityDate'),
    // A rolling note prices itself while live (its yield history is the product); a constitution note's
    // rate belongs to the cycle and is disabled on its own.
    netYield: !rolling && !constitution && fixedByLedger('netYield'),
  };
}

// The label of a locked column: the `system` mark carrying the reason, in place of the open label.
const labelFor = (text, locked, why, open = text) => (locked ? <SystemLabel text={text} why={why} /> : open);

// What a box looks like on THIS note: its label (plain, `sensitive` or `system`), the line under it,
// whether it is disabled and what hangs off its value. Read by the step bodies and by the Review alike.
//
// The LABEL is the investor's own word wherever there is a fact sheet to borrow it from (dashboardMap);
// the reduced set keeps our functional names, because a product the system mints has no per-note sheet —
// there `investmentAmount` stays "Offering size (Alto valuation cap)", which is what Alto reads.
function decorate(name, def, ctx) {
  const { locks, investment, mode } = ctx;
  const editing = mode === 'edit';
  const base = ctx.reduced && def.product ? { ...def, ...def.product } : def;
  // Individual: the fact sheet's word names the box and the map's line explains it. Reduced: our own
  // functional name stays, and a `product.does` override (where the column means something else on a
  // minted product) wins over the map's line.
  const box = dashboardBox(name);
  const text = (!ctx.reduced && box && box.label) || base.label;
  const hint = base.does || (box && box.hint);
  let label = text;
  let disabled = false;
  let extra;

  if (name === 'address' && locks.constitution) {
    label = <SystemLabel text={text} why={SCHEDULER_OWNED} />;
    disabled = true;
  }

  if (name === 'netYield') {
    if (locks.constitution) {
      label = <SystemLabel text={text} why={CYCLE_YIELD_WHY} />;
      disabled = true;
      extra = 'From the cycle configuration (Retool)';
    } else {
      label = labelFor(text, locks.netYield, LEDGER_FIXED_WHY, <SensitiveLabel text={text} why={NET_YIELD_WHY} />);
      disabled = locks.netYield;
      const last = locks.rolling ? (investment?.yieldHistory || []).slice(-1)[0] : undefined;
      if (last) extra = `Last change: ${last.netYield}% on ${moment(last.changedAt).format('LL')}`;
    }
  }

  if (name === 'fundingAmount') {
    label = labelFor(text, locks.fundingAmount, LEDGER_FIXED_WHY, <SensitiveLabel text={text} why={FUNDING_WHY} />);
    disabled = locks.fundingAmount;
  }

  if (name === 'investmentAmount') {
    label = labelFor(text, locks.investmentAmount, LEDGER_FIXED_WHY, <SensitiveLabel text={text} why={INITIAL_AMOUNT_WHY} />);
    disabled = locks.investmentAmount;
  }

  if (name === 'termLength') {
    label = labelFor(text, locks.termLength, LEDGER_FIXED_WHY);
    disabled = locks.termLength;
  }

  if (name === 'maturityDate') {
    label = labelFor(text, locks.maturityDate, LEDGER_FIXED_WHY);
    disabled = locks.maturityDate;
  }

  if (editing && name === 'image') {
    extra = (
      <div style={{ marginTop: 8 }}>
        <NoteImage src={investment?.imageUrl} alt="Property" width={240} />
      </div>
    );
  }
  if (editing && name === 'documents') extra = <StoredDocuments documents={investment?.documents} />;

  if (base.marker && !ctx.reduced) {
    label = (
      <span>
        <Marker n={base.marker} style={{ marginRight: 6 }} />
        {label}
      </span>
    );
  }
  if (hint) label = <BoxLabel hint={hint}>{label}</BoxLabel>;
  return { def: base, label, disabled, extra };
}

function renderInput(name, def, disabled, ctx) {
  switch (def.kind) {
    case 'text':
      return <Input name={name} placeholder={def.placeholder || def.label} disabled={disabled} />;
    case 'textarea':
      return infoInput(name, def.placeholder || def.label);
    case 'currency':
      return currencyInput(def.noDecimal, disabled);
    case 'percent':
      return <Input name={name} placeholder={def.label} addonAfter="%" type="number" disabled={disabled} />;
    case 'months':
      return monthsInput(name, def.label, disabled);
    case 'date':
      return <DatePicker placeholder={def.label} style={{ width: '100%' }} disabled={disabled} />;
    case 'tag':
      return (
        <Select allowClear placeholder="Select here">
          {(def.tagType === 'property' ? ctx.propTags : ctx.loanTags).map((tag) => (
            <Option key={tag._id} value={tag._id}>
              {tag.name}
            </Option>
          ))}
        </Select>
      );
    case 'select':
      return (
        <Select allowClear placeholder="Select here">
          {def.options.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      );
    case 'file':
      return (
        <Upload name={name} listType={def.listType || 'text'} multiple={!!def.multiple} beforeUpload={() => false}>
          <Button icon={<UploadOutlined />}>{def.button}</Button>
        </Upload>
      );
    default:
      return null;
  }
}

export function renderField(name, ctx) {
  const raw = FIELDS[name];
  if (!raw || (raw.editOnly && ctx.mode !== 'edit')) return null;
  const { def, label, disabled, extra } = decorate(name, raw, ctx);

  // Not a box: the note's own update timeline, shown where the operator expects to find it. It carries
  // its own writes (NoteUpdates) instead of riding on the form's Save — an entry is added, edited and
  // removed one at a time, and nothing typed in the wizard travels with it.
  if (def.kind === 'updates') {
    return (
      <Col key={name} {...(def.span || ctx.span)}>
        <Form.Item label={label}>
          <NoteUpdates
            investment={ctx.investment}
            writable={ctx.mode === 'edit' && isTimelineWritable(ctx.investment?.noteType)}
          />
        </Form.Item>
      </Col>
    );
  }

  const fileProps = def.kind === 'file' ? { valuePropName: 'fileList', getValueFromEvent: fileListFromEvent } : {};
  return (
    <Field key={name} name={name} label={label} span={def.span || ctx.span} rules={def.rules} extra={extra} {...fileProps}>
      {renderInput(name, def, disabled, ctx)}
    </Field>
  );
}

export function renderGroup(group, index, ctx) {
  return (
    <Section key={group.title || index} title={group.title || null}>
      {group.fields.map((name) => renderField(name, ctx))}
    </Section>
  );
}

// The real screenshot of the section the step feeds, stuck beside the fields while they scroll.
export function GuidePanel({ step }) {
  const { guide } = step;
  return (
    <div style={{ position: 'sticky', top: 16 }}>
      <Card size="small" title={`How investors see it — ${DASHBOARD_SECTIONS[step.dashboard]}`}>
        <div style={{ position: 'relative', lineHeight: 0 }}>
          <Image src={guide.src} alt={DASHBOARD_SECTIONS[step.dashboard]} width="100%" preview={{ mask: 'Zoom' }} />
          {(guide.markers || []).map((marker) => (
            <Marker
              key={marker.n}
              n={marker.n}
              style={{
                position: 'absolute',
                left: `${marker.left}%`,
                top: `${marker.top}%`,
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 0 2px #fff',
                pointerEvents: 'none',
              }}
            />
          ))}
        </div>
        {guide.note && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            {guide.note}
          </Text>
        )}
      </Card>
    </div>
  );
}

// What the screenshot panel becomes on the reduced set: WHERE the things this form does not ask come
// from. A product the system mints has no per-note fact sheet to shoot, and a capture of the Individual
// Note's would be describing a screen this note never gets.
export function AsidePanel({ step, noteType }) {
  const lines = asideLines(step, noteType);
  if (isEmpty(lines)) return null;
  return (
    <div style={{ position: 'sticky', top: 16 }}>
      <Card
        size="small"
        title={
          <span>
            <LockOutlined style={{ marginRight: 8 }} />
            Not asked here — and why
          </span>
        }
        headStyle={{ background: '#fafafa' }}
      >
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {lines.map((line) => (
            <li key={line} style={{ marginBottom: 8 }}>
              <Text type="secondary">{line}</Text>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// The section's screenshot shrunk to the Review card's header; click = the full capture.
export function GuideThumb({ step }) {
  return (
    <Image
      src={step.guide.src}
      alt={DASHBOARD_SECTIONS[step.dashboard]}
      height={40}
      preview={{ mask: 'Zoom' }}
      style={{ borderRadius: 2 }}
    />
  );
}

// One Card per step with ALL its boxes, three per row: the same Field definitions the steps render, on
